import { toCalendarYmd } from "@/features/opportunities/lib/set-date-window-display";

import type {
  PersonaOpportunitySetDetail,
  PersonaOpportunitySetRow,
} from "../api/persona-opportunity-sets.api";
import type {
  PersonaOpportunitySet,
  PersonaOpportunityTabCounts,
} from "../types/persona-opportunities.types";

/**
 * Set-level mapping: API rows → the view model the sets grid and workspace shell
 * render. Per-opportunity card/table rows are a separate concern (KTD9).
 */

/**
 * Display timestamp for set cards / header age.
 * READY uses `updatedAt` as generated-age until the API exposes `generationCompletedAt`.
 */
export function personaOpportunitySetDisplayTimestamp(set: {
  status: PersonaOpportunitySet["status"];
  createdAt: string;
  updatedAt: string;
}): string {
  return set.status === "READY" ? set.updatedAt : set.createdAt;
}

function toSetBase(row: PersonaOpportunitySetRow) {
  return {
    id: row.id,
    name: row.name,
    tenantId: row.tenantId,
    status: row.status,
    industry: row.config?.industry ?? null,
    industryCompetitors: row.config?.industryCompetitors ?? [],
    // v1 generates against a single platform even though the config stores a list.
    adPlatform: row.config?.adPlatforms?.[0] ?? null,
    startDate: toCalendarYmd(row.startDate),
    endDate: toCalendarYmd(row.endDate),
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
  };
}

/** List row — tab counts stay `null`; footer totals come from Prisma counts. */
export function toPersonaOpportunitySet(row: PersonaOpportunitySetRow): PersonaOpportunitySet {
  return {
    ...toSetBase(row),
    opportunityCount: row.opportunityCount ?? 0,
    scriptCount: row.scriptCount ?? 0,
    counts: null,
  };
}

/**
 * Tab badge counts (KTD11):
 * - `opportunities` = every non-deleted opportunity (all statuses)
 * - `scripts` = persona script entity count (`detail.set.scriptCount`)
 * - `sent_to_vendor` = 0 (placeholder until vendor-scripts epic)
 */
export function countPersonaOpportunityTabs(
  detail: PersonaOpportunitySetDetail,
): PersonaOpportunityTabCounts {
  const opportunities = detail.personas.reduce(
    (total, persona) => total + persona.opportunities.length,
    0,
  );

  return {
    opportunities,
    scripts: detail.set.scriptCount ?? 0,
    sent_to_vendor: 0,
  };
}

function countDetailOpportunities(detail: PersonaOpportunitySetDetail): number {
  return detail.personas.reduce(
    (total, persona) => total + persona.opportunities.length,
    0,
  );
}

export function toPersonaOpportunitySetFromDetail(
  detail: PersonaOpportunitySetDetail,
): PersonaOpportunitySet {
  return {
    ...toSetBase(detail.set),
    opportunityCount: detail.set.opportunityCount ?? countDetailOpportunities(detail),
    scriptCount: detail.set.scriptCount ?? 0,
    counts: countPersonaOpportunityTabs(detail),
  };
}
